export const TERMINAL_COLLECT_TIMEOUT_MS = 3 * 60 * 1000

export const TERMINAL_PAYMENT_STATUS_LABELS = {
  not_ready: 'Getting terminal ready…',
  ready: 'Terminal ready',
  waiting_for_input: 'Waiting for card…',
  processing: 'Processing payment…',
}

export function getPaymentIntentIdFromClientSecret(clientSecret) {
  if (!clientSecret || typeof clientSecret !== 'string') return null
  const [id] = clientSecret.split('_secret_')
  return id?.startsWith('pi_') ? id : null
}

export function isTerminalPaymentCanceledError(error) {
  const code = String(error?.code || '').toLowerCase()
  return code === 'canceled' || code.includes('canceled')
}

export function getTerminalPaymentErrorMessage(
  error,
  { timedOut = false } = {}
) {
  if (timedOut) {
    return 'The terminal timed out waiting for a card. You can try again when ready.'
  }
  if (isTerminalPaymentCanceledError(error)) {
    return 'The terminal payment was canceled. You can try again when ready.'
  }
  return (
    error?.message ||
    "The connection to POS wasn't successful. Please make sure it is connected properly and try again."
  )
}

export async function collectTerminalPaymentMethod(terminal, clientSecret) {
  return terminal.collectPaymentMethod(clientSecret, {
    config_override: {
      enable_customer_cancellation: true,
    },
  })
}

export async function runTerminalPaymentFlow({
  terminal,
  clientSecret,
  isCollectingRef,
  collectTimeoutRef,
  onTimeout,
}) {
  isCollectingRef.current = true

  collectTimeoutRef.current = setTimeout(() => {
    onTimeout?.()
  }, TERMINAL_COLLECT_TIMEOUT_MS)

  try {
    const collectResult = await collectTerminalPaymentMethod(
      terminal,
      clientSecret
    )

    if (collectResult.error) {
      throw collectResult.error
    }

    const processResult = await terminal.processPayment(
      collectResult.paymentIntent
    )

    if (processResult.error) {
      throw processResult.error
    }

    const paymentIntent =
      processResult.paymentIntent || collectResult.paymentIntent
    if (!paymentIntent?.id) {
      throw new Error('Terminal payment completed without a payment intent.')
    }

    const terminalStatus = String(paymentIntent.status || '').toLowerCase()
    if (
      terminalStatus &&
      terminalStatus !== 'succeeded' &&
      terminalStatus !== 'requires_capture'
    ) {
      throw new Error(
        `Terminal payment did not succeed (status: ${paymentIntent.status}).`
      )
    }

    return { paymentIntent }
  } finally {
    isCollectingRef.current = false
    if (collectTimeoutRef.current) {
      clearTimeout(collectTimeoutRef.current)
      collectTimeoutRef.current = null
    }
  }
}
